import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { CATEGORIES, formatApiErrorDetail } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import RichTextEditor from "@/components/RichTextEditor";
import ImageUploader from "@/components/ImageUploader";

export default function AdminEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: "",
    category: "articles",
    excerpt: "",
    content: "",
    cover_image: "",
    status: "draft",
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api
      .get(`/admin/posts/${id}`)
      .then((r) =>
        setForm({
          title: r.data.title || "",
          category: r.data.category || "articles",
          excerpt: r.data.excerpt || "",
          content: r.data.content || "",
          cover_image: r.data.cover_image || "",
          status: r.data.status || "draft",
        }),
      )
      .catch((e) =>
        toast.error(
          formatApiErrorDetail(e?.response?.data?.detail) || "Could not load",
        ),
      )
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (status) => {
    setSaving(true);
    try {
      const payload = { ...form, status: status || form.status };
      if (isEdit) {
        await api.put(`/admin/posts/${id}`, payload);
        toast.success(payload.status === "published" ? "Published" : "Saved");
      } else {
        const { data } = await api.post("/admin/posts", payload);
        toast.success(
          payload.status === "published" ? "Published" : "Draft saved",
        );
        navigate(`/admin/edit/${data.id}`, { replace: true });
        return;
      }
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20" data-testid="editor-loading">
        <p className="text-ink-soft">Loading…</p>
      </div>
    );
  }

  const triggerCls =
    "w-full bg-paper border border-rule rounded-none h-12 px-3 text-sm text-ink hover:border-ink focus:border-ink focus:ring-0";

  return (
    <div className="bg-paper min-h-[80vh]" data-testid="admin-editor-page">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-terracotta mb-8"
          data-testid="editor-back-link"
        >
          <ArrowLeft size={14} /> Dashboard
        </Link>

        <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-4">
          {isEdit ? "Edit Writing" : "New Writing"}
        </p>
        <h1 className="font-serif-display text-3xl lg:text-4xl text-ink tracking-tight">
          {isEdit ? "Polish your words" : "Begin a new piece"}
        </h1>

        <div className="mt-12 space-y-8">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              Title
            </label>
            <input
              value={form.title}
              onChange={set("title")}
              data-testid="editor-title-input"
              className="w-full bg-transparent border-b border-rule focus:border-ink py-3 text-2xl font-serif-display text-ink outline-none"
              placeholder="A title for your writing…"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
                Category
              </label>
              <Select value={form.category} onValueChange={setVal("category")}>
                <SelectTrigger
                  data-testid="editor-category-select"
                  className={triggerCls}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem
                      key={c.key}
                      value={c.key}
                      data-testid={`editor-category-option-${c.key}`}
                    >
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
                Status
              </label>
              <Select value={form.status} onValueChange={setVal("status")}>
                <SelectTrigger
                  data-testid="editor-status-select"
                  className={triggerCls}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft" data-testid="editor-status-option-draft">
                    Draft
                  </SelectItem>
                  <SelectItem
                    value="published"
                    data-testid="editor-status-option-published"
                  >
                    Published
                  </SelectItem>
                  <SelectItem
                    value="notes"
                    data-testid="editor-status-option-notes"
                  >
                    Notes (private)
                  </SelectItem>
                  <SelectItem
                    value="archive"
                    data-testid="editor-status-option-archive"
                  >
                    Archive (private)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <ImageUploader
            value={form.cover_image}
            onChange={setVal("cover_image")}
            label="Cover Image (optional)"
            testIdPrefix="editor-cover"
          />

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              Excerpt
            </label>
            <textarea
              rows={3}
              value={form.excerpt}
              onChange={set("excerpt")}
              data-testid="editor-excerpt-input"
              className="w-full bg-paper border border-rule px-4 py-3 text-base text-ink leading-relaxed outline-none focus:border-ink font-serif-display italic"
              placeholder="A short, evocative summary…"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              Body
            </label>
            <RichTextEditor
              value={form.content}
              onChange={setVal("content")}
              placeholder="Write the piece here. Use the toolbar above for headings, emphasis, quotes, and links."
            />
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-rule">
            <button
              onClick={() => save("draft")}
              disabled={saving || !form.title}
              data-testid="editor-save-draft-btn"
              className="px-5 py-3 border border-ink text-ink text-xs tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors disabled:opacity-50"
            >
              Save as draft
            </button>
            <button
              onClick={() => save("published")}
              disabled={saving || !form.title}
              data-testid="editor-publish-btn"
              className="px-5 py-3 bg-terracotta text-paper text-xs tracking-[0.18em] uppercase hover:bg-ink transition-colors disabled:opacity-50"
            >
              {form.status === "published" ? "Update" : "Publish"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
